import type { FreightEmail, FreightTriageItem } from "@/types/models";
import { slaDueFrom } from "@/lib/sla";

const hoursAgo = (h: number) => new Date(Date.now() - h * 3600_000).toISOString();

export const OWNERS = ["Vijay Kumar", "Rajaram S", "Priyanka N", "Shiva R"];

// Timestamps are computed relative to "now" so SLA/ageing states stay realistic
// every time the app loads (great for a live demo).
function buildItems(): FreightTriageItem[] {
  return [
    {
      id: "t1",
      threadId: "th-112233",
      name: "Ocean shipment update — SH-2024-112233 / 112234 (Rotterdam → Singapore)",
      shNumbers: ["SH-2024-112233", "SH-2024-112234"],
      carrier: "Kuehne+Nagel",
      mode: "Ocean",
      hbl: "HBL-KN-2024-0341",
      mbl: "MBLKN2024112",
      toList: "freightbookings@te.com",
      ccList: "vijay@te.com, kanokwan@te.com",
      latestStatus:
        "SH-2024-112233 loaded on MSC ELISA, ETD Rotterdam 14-Nov, ETA Singapore 02-Dec. SH-2024-112234 awaiting origin customs, est. departure 16-Nov.",
      priority: "Medium",
      state: "Queue",
      needsAttention: false,
      firstEmailAt: hoursAgo(30),
      latestEmailAt: hoursAgo(6),
      slaDueAt: slaDueFrom(hoursAgo(30), "Medium"),
    },
    {
      id: "t2",
      threadId: "th-109876",
      name: "URGENT — SH-2024-109876 held at Frankfurt customs (penalty accruing)",
      shNumbers: ["SH-2024-109876"],
      carrier: "DHL",
      mode: "AIR",
      awb: "AWB-1234-56789",
      toList: "freightbookings@te.com",
      ccList: "vijay@te.com",
      latestStatus:
        "URGENT — SH-2024-109876 (DHL Express) held at Frankfurt customs pending docs. EUR 150/day penalty accruing. Needs immediate document submission.",
      priority: "High",
      state: "Queue",
      needsAttention: true,
      firstEmailAt: hoursAgo(20),
      latestEmailAt: hoursAgo(20),
      slaDueAt: slaDueFrom(hoursAgo(20), "High"),
    },
    {
      id: "t3",
      threadId: "th-113344",
      name: "Delivered — SH-2024-113344 to Pune DC (POD attached)",
      shNumbers: ["SH-2024-113344"],
      carrier: "Panalpina",
      mode: "Courier",
      awb: "AWB-7896-00123",
      toList: "freightbookings@te.com",
      ccList: "priyanka@te.com",
      latestStatus: "SH-2024-113344 (FedEx via Panalpina) delivered to Pune DC 12-Nov 14:35 local. POD attached.",
      priority: "Medium",
      state: "Resolved",
      assignee: "Priyanka N",
      needsAttention: false,
      firstEmailAt: hoursAgo(40),
      latestEmailAt: hoursAgo(38),
      slaDueAt: slaDueFrom(hoursAgo(40), "Medium"),
      resolvedAt: hoursAgo(2),
    },
    {
      id: "t4",
      threadId: "th-110045",
      name: "Multi-mode update — SH-2024-110045 (Ocean) / 110046 (Road)",
      shNumbers: ["SH-2024-110045", "SH-2024-110046"],
      carrier: "Geodis",
      mode: "Ocean",
      hbl: "HBL-GEO-2024-0045",
      toList: "freightbookings@te.com",
      ccList: "",
      latestStatus:
        "SH-2024-110045 cleared customs, ETA DC 20-Nov. SH-2024-110046 (Road) departed origin, est. delivery 15-Nov.",
      priority: "Medium",
      state: "Assigned",
      assignee: "Rajaram S",
      needsAttention: false,
      firstEmailAt: hoursAgo(10),
      latestEmailAt: hoursAgo(3),
      slaDueAt: slaDueFrom(hoursAgo(10), "Medium"),
    },
    {
      id: "t5",
      threadId: "th-110567",
      name: "URGENT — SH-2024-110567 ON HOLD at Shanghai (Certificate of Origin missing)",
      shNumbers: ["SH-2024-110567"],
      carrier: "DB Schenker",
      mode: "Ocean",
      hbl: "HBL-DBS-2024-0567",
      mbl: "MBLTE2024998",
      toList: "freightbookings@te.com",
      ccList: "kanokwan@te.com, vijay@te.com",
      latestStatus:
        "URGENT — SH-2024-110567 ON HOLD at Shanghai; Certificate of Origin missing. USD 250/day penalty. Escalation required.",
      priority: "High",
      state: "Queue",
      needsAttention: true,
      firstEmailAt: hoursAgo(26),
      latestEmailAt: hoursAgo(26),
      slaDueAt: slaDueFrom(hoursAgo(26), "High"),
    },
    {
      id: "t6",
      threadId: "th-114455",
      name: "Booking confirmed — SH-2024-114455 (Ocean, Maersk)",
      shNumbers: ["SH-2024-114455"],
      carrier: "Maersk",
      mode: "Ocean",
      hbl: "HBL-MAE-2024-0455",
      toList: "freightbookings@te.com",
      ccList: "",
      latestStatus: "SH-2024-114455 booking confirmed, container gated in. ETD 18-Nov ex Ningbo.",
      priority: "Medium",
      state: "Queue",
      needsAttention: false,
      firstEmailAt: hoursAgo(5),
      latestEmailAt: hoursAgo(5),
      slaDueAt: slaDueFrom(hoursAgo(5), "Medium"),
    },
  ];
}

function buildEmails(): FreightEmail[] {
  return [
    {
      id: "e1",
      threadId: "th-112233",
      from: "ops@kuehne-nagel-demo.com",
      to: "freightbookings@te.com",
      cc: "vijay@te.com, kanokwan@te.com",
      subject: "Ocean shipment update — SH-2024-112233 / 112234",
      body:
        "Hello TE team,\n\nUpdate on your ocean shipments:\n\nSH-2024-112233 (HBL-KN-2024-0341) has been loaded on MSC ELISA. ETD Rotterdam 14-Nov, ETA Singapore 02-Dec.\nSH-2024-112234 is awaiting origin customs clearance, estimated departure 16-Nov.\n\nWe will keep you posted on any changes.\n\nBest regards,\nKuehne+Nagel Ocean Desk",
      receivedAt: hoursAgo(30),
      hasAttachment: false,
      processed: true,
    },
    {
      id: "e2",
      threadId: "th-112233",
      from: "ops@kuehne-nagel-demo.com",
      to: "freightbookings@te.com",
      cc: "vijay@te.com",
      subject: "RE: Ocean shipment update — SH-2024-112234 customs",
      body:
        "Quick follow-up — SH-2024-112234 customs paperwork is being reviewed; we expect release within 48 hours. No change to SH-2024-112233.\n\nKuehne+Nagel Ocean Desk",
      receivedAt: hoursAgo(6),
      hasAttachment: false,
      processed: true,
    },
    {
      id: "e3",
      threadId: "th-109876",
      from: "escalations@dhl-freight-demo.com",
      to: "freightbookings@te.com",
      cc: "vijay@te.com",
      subject: "URGENT — SH-2024-109876 held at Frankfurt customs",
      body:
        "URGENT.\n\nSH-2024-109876 (AWB-1234-56789, DHL Express AIR) is held at Frankfurt customs pending the commercial invoice and certificate of conformity. A demurrage penalty of EUR 150/day is now accruing.\n\nPlease submit the missing documents as soon as possible to avoid further charges.\n\nDHL Freight Escalations",
      receivedAt: hoursAgo(20),
      hasAttachment: true,
      processed: true,
    },
    {
      id: "e4",
      threadId: "th-113344",
      from: "bookings@panalpina-demo.com",
      to: "freightbookings@te.com",
      cc: "priyanka@te.com",
      subject: "Delivered — SH-2024-113344 to Pune DC",
      body:
        "Good news — SH-2024-113344 (FedEx via Panalpina, AWB-7896-00123) was delivered to the Pune DC on 12-Nov at 14:35 local time. Proof of delivery is attached.\n\nPanalpina Courier Desk",
      receivedAt: hoursAgo(40),
      hasAttachment: true,
      processed: true,
    },
    {
      id: "e5",
      threadId: "th-110045",
      from: "freight@geodis-demo.com",
      to: "freightbookings@te.com",
      cc: "",
      subject: "Multi-mode update — SH-2024-110045 / 110046",
      body:
        "Update:\n\nSH-2024-110045 (Ocean, HBL-GEO-2024-0045) has cleared customs, ETA DC 20-Nov.\nSH-2024-110046 (Road) departed origin, estimated delivery 15-Nov.\n\nGeodis Multimodal",
      receivedAt: hoursAgo(10),
      hasAttachment: false,
      processed: true,
    },
    {
      id: "e6",
      threadId: "th-110045",
      from: "freight@geodis-demo.com",
      to: "freightbookings@te.com",
      cc: "rajaram@te.com",
      subject: "RE: Multi-mode update — SH-2024-110046 delivery window",
      body: "SH-2024-110046 is on schedule for 15-Nov delivery, AM window. Driver details to follow.\n\nGeodis Multimodal",
      receivedAt: hoursAgo(3),
      hasAttachment: false,
      processed: true,
    },
    {
      id: "e7",
      threadId: "th-110567",
      from: "escalations@db-schenker-demo.com",
      to: "freightbookings@te.com",
      cc: "kanokwan@te.com, vijay@te.com",
      subject: "URGENT — SH-2024-110567 ON HOLD at Shanghai",
      body:
        "URGENT — ESCALATION REQUIRED.\n\nSH-2024-110567 (Ocean, HBL-DBS-2024-0567, MBLTE2024998) is ON HOLD at Shanghai port. The Certificate of Origin is missing and a penalty of USD 250/day is accruing.\n\nWe need TE to provide the Certificate of Origin urgently to release the container.\n\nDB Schenker Escalations",
      receivedAt: hoursAgo(26),
      hasAttachment: false,
      processed: true,
    },
    {
      id: "e8",
      threadId: "th-114455",
      from: "bookings@maersk-demo.com",
      to: "freightbookings@te.com",
      cc: "",
      subject: "Booking confirmed — SH-2024-114455",
      body:
        "Your booking SH-2024-114455 (HBL-MAE-2024-0455) is confirmed. Container gated in at Ningbo; ETD 18-Nov. No action needed at this time.\n\nMaersk Bookings",
      receivedAt: hoursAgo(5),
      hasAttachment: false,
      processed: false,
    },
  ];
}

export const seed = { buildItems, buildEmails };
